import { zodResolver } from '@hookform/resolvers/zod';
import { AttachFile, Close } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, get, useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';
import {
    useAddItineraryAttachmentsMutation,
    useEditReservationMutation,
    useGetAllAssetsQuery,
    useGetAllReservationsQuery,
    useGetIndividualAssetQuery,
    useGetIndividualReservationQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import {
    Job,
    ModuleType,
    ProviderType,
} from '../../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../../shared/AuthRoles';
import AutoHeightTransition from '../../../../../shared/AutoHeightTransition';
import { theme } from '../../../../../shared/theme';
import { getDateArray } from '../../../../../shared/utils';
import { Status } from '../../../../enums/booking';
import { ServiceType, WeekDays } from '../../../../enums/contractor';
import { AvailabilityMode } from '../../../../enums/experience';
import { ItineraryItemType } from '../../../../enums/itinerary';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import FormDateFilter from '../../../../shared/FormDateFilter';
import FormSelect from '../../../../shared/FormSelect';
import FormTextField from '../../../../shared/FormTextField';
import FormUpload from '../../../../shared/FormUpload';
import toast from '../../../../shared/Toast';
import { ModuleContractor } from '../../../../types/contractor';
import { FormValues as Experience } from '../../../../types/experience';
import {
    ItineraryItemDto,
    ItineraryItemPayload,
} from '../../../../types/itinerary';
import { SetMenu } from '../../../../types/set-menu';
import { Media } from '../../../../types/user';
import {
    formatHirePeriod,
    formatMaxHirePeriod,
} from '../../../Experiences/IndividualExperienceDetails';
import { TimeArray } from '../../../Experiences/Owner/Property/TimeArray';
import { shouldShowOptionalServicesSection } from '../../EditReservation/optionalServiceAvailability';
import ContractorPolicies from '../../ReservationRequest/ReservationJobs/ContractorPolicies';
import ReservationJobForm, {
    SetMenuAsProvider,
} from '../../ReservationRequest/ReservationJobs/ReservationJobForm';
import {
    OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL,
    OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
} from '../../ReservationRequest/ReservationJobs/reservationJobs.helpers';
import { jobSchema } from '../../ReservationRequest/Shared/schema';
import AddOnList from '../Addons/AddOnList';
import OptionalServiceList from '../Addons/OptionalServiceList';
import { ViewKey } from '../ViewIndividualReservation';
import EventForm from './EventForm';
import ExperiencesList from './ExperiencesList';
import FlightForm from './FlightForm';
import { ItemTypeOptions } from './itineraryConstants';
import OtherForm from './OtherForm';
import ReservationForm from './ReservationForm';
import RestaurantForm from './RestaurantForm';
import TransferForm from './TransferForm';

interface ItineraryItemFormProps {
    open: boolean;
    onClose: (targetTab?: ViewKey) => void;
    onSubmit: (payload: ItineraryItemPayload | ItineraryItemFormValues) => void;
    existingItem?: ItineraryItemDto | null;
    initialDate?: string | null;
    mainBooking: any;
    isAddOnForm: boolean;
    hasAddOns?: boolean;
    setTab?: (tab: ViewKey) => void;
    preselectedAddOn?: { id: number; type: string } | null;
    preselectedPartnerService?: boolean;
}

const defaultValues = {
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    notes: '',
    refNumber: '',
    attachments: [],
    address: {
        streetAddress: '',
        suburb: '',
        state: '',
        countryCode: '',
        postcode: '',
    },
    itineraryItemInfo: {
        itemType: ItineraryItemType.Flight,
        airline: '',
        flightNumber: '',
        gate: '',
        seatNumber: '',
        departureAirport: {
            code: '',
            name: '',
            terminal: '',
        },
        arrivalAirport: {
            code: '',
            name: '',
            terminal: '',
        },
        title: '',
        venue: '',
        dressCode: '',
        restaurantName: '',
        reservationName: '',
        provider: '',
        vehicleType: '',
        pickupType: 'airport' as const,
        pickupAirport: {
            code: '',
            name: '',
            terminal: '',
        },
        pickupAddress: {
            streetAddress: '',
            suburb: '',
            state: '',
            countryCode: '',
            postcode: '',
        },
        dropoffType: 'address' as const,
        dropoffAirport: {
            code: '',
            name: '',
            terminal: '',
        },
        dropoffAddress: {
            streetAddress: '',
            suburb: '',
            state: '',
            countryCode: '',
            postcode: '',
        },
    },
};

const schema = z
    .object({
        existingReservation: z.coerce.number().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
        jobs: jobSchema.optional(),
        notes: z.string().optional(),
        refNumber: z.string().optional(),
        address: z
            .object({
                streetAddress: z.string().optional(),
                suburb: z.string().optional(),
                state: z.string().optional(),
                countryCode: z.string().optional(),
                postcode: z.string().optional(),
                lat: z.number().optional(),
                lng: z.number().optional(),
            })
            .optional(),
        attachments: z.array(z.any()).optional().default([]),
        itineraryItemInfo: z.object({
            itemType: z.string().min(1, 'Item type is required'),
            experienceId: z.number().optional(),
            reservationFormState: z.any().optional(),
            airline: z.string().optional(),
            flightNumber: z.string().optional(),
            gate: z.string().optional(),
            seatNumber: z.string().optional(),
            departureAirport: z
                .object({
                    code: z.string().optional(),
                    name: z.string().optional(),
                    terminal: z.string().optional(),
                })
                .optional(),
            arrivalAirport: z
                .object({
                    code: z.string().optional(),
                    name: z.string().optional(),
                    terminal: z.string().optional(),
                })
                .optional(),
            title: z.string().optional(),
            venue: z.string().optional(),
            dressCode: z.string().optional(),
            restaurantName: z.string().optional(),
            reservationName: z.string().optional(),
            provider: z.string().optional(),
            vehicleType: z.string().optional(),
            pickupType: z.enum(['airport', 'address']).optional(),
            pickupAirport: z
                .object({
                    code: z.string().optional(),
                    name: z.string().optional(),
                    terminal: z.string().optional(),
                })
                .optional(),
            pickupAddress: z
                .object({
                    streetAddress: z.string().optional(),
                    suburb: z.string().optional(),
                    state: z.string().optional(),
                    countryCode: z.string().optional(),
                    postcode: z.string().optional(),
                    lat: z.number().optional(),
                    lng: z.number().optional(),
                })
                .optional(),
            dropoffType: z.enum(['airport', 'address']).optional(),
            dropoffAirport: z
                .object({
                    code: z.string().optional(),
                    name: z.string().optional(),
                    terminal: z.string().optional(),
                })
                .optional(),
            dropoffAddress: z
                .object({
                    streetAddress: z.string().optional(),
                    suburb: z.string().optional(),
                    state: z.string().optional(),
                    countryCode: z.string().optional(),
                    postcode: z.string().optional(),
                    lat: z.number().optional(),
                    lng: z.number().optional(),
                })
                .optional(),
        }),
    })
    .superRefine((data, ctx) => {
        const isPartnerServices =
            data.itineraryItemInfo.itemType ===
            ItineraryItemType.PartnerService;

        if (isPartnerServices) {
            if (!data.jobs) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['jobs'],
                });
            }
        } else {
            if (!data.startDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['startDate'],
                });
            }
            if (!data.endDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['endDate'],
                });
            }
            if (!data.startTime) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['startTime'],
                });
            }
            if (!data.endTime) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['endTime'],
                });
            }
        }

        switch (data.itineraryItemInfo.itemType) {
            case ItineraryItemType.Flight:
                if (!data.itineraryItemInfo.departureAirport?.name?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['itineraryItemInfo', 'departureAirport', 'name'],
                    });
                }
                if (!data.itineraryItemInfo.arrivalAirport?.name?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['itineraryItemInfo', 'arrivalAirport', 'name'],
                    });
                }
                break;
            case ItineraryItemType.Transfer:
                if (data.itineraryItemInfo.pickupType === 'airport') {
                    if (!data.itineraryItemInfo.pickupAirport?.name?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAirport',
                                'name',
                            ],
                        });
                    }
                } else if (data.itineraryItemInfo.pickupType === 'address') {
                    if (
                        !data.itineraryItemInfo.pickupAddress?.streetAddress?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAddress',
                                'streetAddress',
                            ],
                        });
                    }
                    if (!data.itineraryItemInfo.pickupAddress?.suburb?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAddress',
                                'suburb',
                            ],
                        });
                    }
                    if (!data.itineraryItemInfo.pickupAddress?.state?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAddress',
                                'state',
                            ],
                        });
                    }
                    if (
                        !data.itineraryItemInfo.pickupAddress?.postcode?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAddress',
                                'postcode',
                            ],
                        });
                    }
                    if (
                        !data.itineraryItemInfo.pickupAddress?.countryCode?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'pickupAddress',
                                'countryCode',
                            ],
                        });
                    }
                }

                if (data.itineraryItemInfo.dropoffType === 'airport') {
                    if (!data.itineraryItemInfo.dropoffAirport?.name?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAirport',
                                'name',
                            ],
                        });
                    }
                } else if (data.itineraryItemInfo.dropoffType === 'address') {
                    if (
                        !data.itineraryItemInfo.dropoffAddress?.streetAddress?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAddress',
                                'streetAddress',
                            ],
                        });
                    }
                    if (
                        !data.itineraryItemInfo.dropoffAddress?.suburb?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAddress',
                                'suburb',
                            ],
                        });
                    }
                    if (!data.itineraryItemInfo.dropoffAddress?.state?.trim()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAddress',
                                'state',
                            ],
                        });
                    }
                    if (
                        !data.itineraryItemInfo.dropoffAddress?.postcode?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAddress',
                                'postcode',
                            ],
                        });
                    }
                    if (
                        !data.itineraryItemInfo.dropoffAddress?.countryCode?.trim()
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Required',
                            path: [
                                'itineraryItemInfo',
                                'dropoffAddress',
                                'countryCode',
                            ],
                        });
                    }
                }
                break;
            case ItineraryItemType.Event:
            case ItineraryItemType.Custom:
                if (!data.itineraryItemInfo.title?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['itineraryItemInfo', 'title'],
                    });
                }
                break;
            case ItineraryItemType.Restaurant:
                if (!data.itineraryItemInfo.restaurantName?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['itineraryItemInfo', 'restaurantName'],
                    });
                }
                break;
        }

        if (
            data.itineraryItemInfo.itemType === ItineraryItemType.Event ||
            data.itineraryItemInfo.itemType === ItineraryItemType.Restaurant ||
            data.itineraryItemInfo.itemType === ItineraryItemType.Custom
        ) {
            const hasAnyAddressValue = Boolean(
                data.address?.streetAddress?.trim() ||
                data.address?.suburb?.trim() ||
                data.address?.state?.trim() ||
                data.address?.postcode?.trim() ||
                data.address?.countryCode?.trim(),
            );

            if (hasAnyAddressValue) {
                if (!data.address?.streetAddress?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['address', 'streetAddress'],
                    });
                }
                if (!data.address?.suburb?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['address', 'suburb'],
                    });
                }
                if (!data.address?.state?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['address', 'state'],
                    });
                }
                if (!data.address?.postcode?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['address', 'postcode'],
                    });
                }
                if (!data.address?.countryCode?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['address', 'countryCode'],
                    });
                }
            }
        }
    });

type FormData = z.infer<typeof schema>;
export type ItineraryItemFormValues = FormData;

const createEmptyPartnerJob = (
    providers: (ModuleContractor | SetMenuAsProvider)[],
) => {
    const loggedInUser = UserRole();
    const singleProvider = providers.length === 1 ? providers[0] : null;
    const contractorId = singleProvider?.id ?? '';
    const isSetMenuProvider =
        contractorId === OWNER_SET_MENU_CONTRACTOR_ID_CATERING ||
        contractorId === OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL;
    return {
        contractor: {
            id: contractorId,
        },
        providerType: isSetMenuProvider
            ? ProviderType.Owner
            : ProviderType.Contractor,
        jobDate: '',
        startTime: null,
        endTime: null,
        ownerNotes: '',
        guestNotes: '',
        items: [],
        services: [],
        templateId: undefined,
        newJob: true,
        contractorHasTemplates: false,
        createdBy: loggedInUser?.role.includes('guest')
            ? ('guest' as const)
            : ('owner' as const),

        policy: { checked: false },
    };
};

const ownerSetMenuContractorId = (serviceType: ServiceType) =>
    serviceType === ServiceType.Alcohol
        ? OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
        : OWNER_SET_MENU_CONTRACTOR_ID_CATERING;

const toFormDefaults = (data: ItineraryItemDto): FormData => {
    const info = data.itineraryItemInfo;
    const base: FormData = {
        itineraryItemInfo: {
            itemType: info.itemType,
        },
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime,
        endTime: data.endTime,
        notes: data.notes ?? '',
        refNumber: data.refNumber ?? '',
        address: {
            ...defaultValues.address,
            streetAddress: data.address?.streetAddress ?? '',
            suburb: data.address?.suburb ?? '',
            state: data.address?.state ?? '',
            countryCode: data.address?.countryCode ?? '',
            postcode: data.address?.postcode ?? '',
            lat: data.address?.lat,
            lng: data.address?.lng,
        },
        attachments: (data.attachments ?? []).map((a) => ({
            id: a.uuid,
            src: a.src,
            uuid: a.uuid,
            name: a.name,
        })),
    };

    switch (info.itemType) {
        case ItineraryItemType.Flight:
            return {
                ...base,
                itineraryItemInfo: {
                    ...base.itineraryItemInfo,
                    airline: info.airline ?? '',
                    flightNumber: info.flightNumber ?? '',
                    gate: info.gate ?? '',
                    seatNumber: info.seatNumber ?? '',
                    departureAirport: {
                        ...defaultValues.itineraryItemInfo.departureAirport,
                        code: info.departureAirport?.code ?? '',
                        name: info.departureAirport?.name ?? '',
                        terminal: info.departureAirport?.terminal ?? '',
                    },
                    arrivalAirport: {
                        ...defaultValues.itineraryItemInfo.arrivalAirport,
                        code: info.arrivalAirport?.code ?? '',
                        name: info.arrivalAirport?.name ?? '',
                        terminal: info.arrivalAirport?.terminal ?? '',
                    },
                },
            };
        case ItineraryItemType.Event:
            return {
                ...base,
                itineraryItemInfo: {
                    ...base.itineraryItemInfo,
                    title: info.title,
                    venue: info.venue ?? '',
                    dressCode: info.dressCode ?? '',
                },
            };
        case ItineraryItemType.Restaurant:
            return {
                ...base,
                itineraryItemInfo: {
                    ...base.itineraryItemInfo,
                    restaurantName: info.restaurantName,
                    reservationName: info.reservationName ?? '',
                },
            };
        case ItineraryItemType.Transfer:
            return {
                ...base,
                itineraryItemInfo: {
                    ...base.itineraryItemInfo,
                    provider: info.provider ?? '',
                    vehicleType: info.vehicleType ?? '',
                    pickupType: info.pickupAddress ? 'address' : 'airport',
                    pickupAirport: {
                        ...defaultValues.itineraryItemInfo.pickupAirport,
                        code: info.pickupAirport?.code ?? '',
                        name: info.pickupAirport?.name ?? '',
                        terminal: info.pickupAirport?.terminal ?? '',
                    },
                    pickupAddress: {
                        ...defaultValues.itineraryItemInfo.pickupAddress,
                        streetAddress: info.pickupAddress?.streetAddress ?? '',
                        suburb: info.pickupAddress?.suburb ?? '',
                        state: info.pickupAddress?.state ?? '',
                        countryCode: info.pickupAddress?.countryCode ?? '',
                        postcode: info.pickupAddress?.postcode ?? '',
                        lat: info.pickupAddress?.lat,
                        lng: info.pickupAddress?.lng,
                    },
                    dropoffType: info.dropoffAddress ? 'address' : 'airport',
                    dropoffAirport: {
                        ...defaultValues.itineraryItemInfo.dropoffAirport,
                        code: info.dropoffAirport?.code ?? '',
                        name: info.dropoffAirport?.name ?? '',
                        terminal: info.dropoffAirport?.terminal ?? '',
                    },
                    dropoffAddress: {
                        ...defaultValues.itineraryItemInfo.dropoffAddress,
                        streetAddress: info.dropoffAddress?.streetAddress ?? '',
                        suburb: info.dropoffAddress?.suburb ?? '',
                        state: info.dropoffAddress?.state ?? '',
                        countryCode: info.dropoffAddress?.countryCode ?? '',
                        postcode: info.dropoffAddress?.postcode ?? '',
                        lat: info.dropoffAddress?.lat,
                        lng: info.dropoffAddress?.lng,
                    },
                },
            };
        case ItineraryItemType.Custom:
            return {
                ...base,
                itineraryItemInfo: {
                    ...base.itineraryItemInfo,
                    title: info.title,
                },
            };
        case ItineraryItemType.PartnerService:
            return info.providerType === ProviderType.Owner
                ? {
                      ...base,
                      itineraryItemInfo: {
                          ...base.itineraryItemInfo,
                      },
                      jobs: {
                          id: info.id,
                          contractor: {
                              id: ownerSetMenuContractorId(info.serviceType),
                          },
                          jobDate: data.startDate,
                          startTime: data.startTime || null,
                          endTime: data.endTime || null,
                          ownerNotes: info.ownerNotes ?? '',
                          guestNotes: info.guestNotes ?? '',
                          providerType: ProviderType.Owner,
                          items: [],
                          services: [],
                          setMenu: {
                              id: info.setMenu.id,
                              name: info.setMenu.name,
                          },
                          templateId: info.setMenu.id,
                          quantity: info.quantity,
                          serviceType: info.serviceType,
                          status: info.status,
                      },
                  }
                : {
                      ...base,
                      itineraryItemInfo: {
                          ...base.itineraryItemInfo,
                      },
                      jobs: {
                          ...base,
                          id: info.id,
                          contractor: info.contractor,
                          jobDate: data.startDate,
                          startTime: data.startTime || null,
                          endTime: data.endTime || null,
                          ownerNotes: info.ownerNotes ?? '',
                          guestNotes: info.guestNotes ?? '',
                          providerType: ProviderType.Contractor,
                          items: info.items ?? [],
                          services: info.services ?? [],
                          templateId: info.items?.[0]?.template?.id,
                          contractorHasTemplates: info.contractorHasTemplates,
                          createdBy: info.createdBy,
                          serviceType: info.serviceType,
                          status: info.status,
                          policy: info.policy,
                      },
                  };

        default:
            return base;
    }
};

const buildAirport = (code?: string, name?: string, terminal?: string) => {
    return { code: code || '', name: name || '', terminal: terminal || '' };
};

const buildAddress = (address: {
    streetAddress?: string;
    suburb?: string;
    state?: string;
    countryCode?: string;
    postcode?: string;
    lat?: number;
    lng?: number;
}) => {
    if (
        !address?.streetAddress &&
        !address?.suburb &&
        !address?.state &&
        !address?.countryCode &&
        !address?.postcode
    )
        return undefined;
    return {
        streetAddress: address.streetAddress || '',
        suburb: address.suburb || '',
        state: address.state || '',
        countryCode: address.countryCode || '',
        postcode: address.postcode || '',
        lat: address.lat,
        lng: address.lng,
    };
};

const buildPayload = (data: FormData): ItineraryItemPayload => {
    const base = {
        startDate: data.startDate ?? '',
        endDate: data.endDate ?? '',
        startTime: data.startTime ?? '',
        endTime: data.endTime ?? '',
        notes: data.notes,
        refNumber: data.refNumber,
        address: data.address ? buildAddress(data.address) : undefined,
        attachments: data.attachments,
    };

    const info = data.itineraryItemInfo;

    switch (info.itemType) {
        case ItineraryItemType.Flight:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Flight,
                    airline: info.airline,
                    flightNumber: info.flightNumber,
                    gate: info.gate,
                    seatNumber: info.seatNumber,
                    departureAirport: buildAirport(
                        info.departureAirport?.code,
                        info.departureAirport?.name,
                        info.departureAirport?.terminal,
                    ),
                    arrivalAirport: buildAirport(
                        info.arrivalAirport?.code,
                        info.arrivalAirport?.name,
                        info.arrivalAirport?.terminal,
                    ),
                },
            };
        case ItineraryItemType.Event:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Event,
                    title: info.title ?? '',
                    venue: info.venue,
                    dressCode: info.dressCode,
                },
            };
        case ItineraryItemType.Restaurant:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Restaurant,
                    restaurantName: info.restaurantName ?? '',
                    reservationName: info.reservationName,
                },
            };
        case ItineraryItemType.Transfer:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Transfer,
                    provider: info.provider,
                    vehicleType: info.vehicleType,
                    pickupAirport:
                        info.pickupType === 'airport'
                            ? buildAirport(
                                  info.pickupAirport?.code,
                                  info.pickupAirport?.name,
                                  info.pickupAirport?.terminal,
                              )
                            : undefined,
                    pickupAddress:
                        info.pickupType === 'address' && info.pickupAddress
                            ? buildAddress(info.pickupAddress)
                            : undefined,
                    dropoffAirport:
                        info.dropoffType === 'airport'
                            ? buildAirport(
                                  info.dropoffAirport?.code,
                                  info.dropoffAirport?.name,
                                  info.dropoffAirport?.terminal,
                              )
                            : undefined,
                    dropoffAddress:
                        info.dropoffType === 'address' && info.dropoffAddress
                            ? buildAddress(info.dropoffAddress)
                            : undefined,
                },
            };
        case ItineraryItemType.Custom:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Custom,
                    title: info.title ?? '',
                },
            };
        default:
            return {
                ...base,
                itineraryItemInfo: {
                    itemType: ItineraryItemType.Custom,
                    title: '',
                },
            };
    }
};

const ItineraryItemForm = ({
    open,
    onClose,
    onSubmit,
    existingItem,
    initialDate,
    mainBooking,
    isAddOnForm,
    hasAddOns = false,
    setTab,
    preselectedAddOn,
    preselectedPartnerService = false,
}: ItineraryItemFormProps) => {
    const [addOnIsSelected, setAddOnIsSelected] = useState<boolean>(
        !!preselectedAddOn || preselectedPartnerService,
    );
    const isEditing = !!existingItem;
    const is7DayEdit = isEditing && existingItem?.is7DayBooking;
    const linkedBookingId = is7DayEdit
        ? (existingItem.itineraryItemInfo as any)?.bookingId
        : undefined;

    const [editReservation] = useEditReservationMutation();
    const [addItineraryAttachments] = useAddItineraryAttachmentsMutation();
    const reservationSubmitRef = useRef<(() => Promise<void>) | null>(null);
    const [isReservationSubmitting, setIsReservationSubmitting] =
        useState(false);

    const methods = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues,
    });

    const {
        reset,
        control,
        watch,
        setValue,
        unregister,
        handleSubmit,
        formState: { isSubmitting, isDirty, errors },
    } = methods;

    const existingReservation = watch('existingReservation');

    const itemType = watch('itineraryItemInfo.itemType');
    const startDate = watch('startDate');
    const attachments = watch('attachments') || [];

    const is7Day = [
        ItineraryItemType.Vehicle,
        ItineraryItemType.Boat,
        ItineraryItemType.Activity,
    ].includes(itemType as ItineraryItemType);

    const isPartnerService = itemType === ItineraryItemType.PartnerService;

    const experienceId = useWatch({
        control,
        name: 'itineraryItemInfo.experienceId',
    });
    const jobContractorId = useWatch({
        control,
        name: 'jobs.contractor.id',
    });
    const jobs = useWatch({
        control,
        name: 'jobs',
    });

    const isExistingReservation = itemType === 'existingReservation';

    const { data: linkedReservation, isFetching: isFetchingReservation } =
        useGetIndividualReservationQuery(`/bookings/${linkedBookingId}`, {
            skip: !linkedBookingId,
        });

    const { data: allReservations, isFetching: isFetchingReservations } =
        useGetAllReservationsQuery(
            {
                sort: 'startDate',
                scheduledFrom: moment(mainBooking?.startDate)
                    .subtract(1, 'day')
                    .format('YYYY-MM-DD'),
                scheduledTo: moment(mainBooking?.endDate)
                    .add(1, 'day')
                    .format('YYYY-MM-DD'),
                moduleType: [
                    ModuleType.Boat,
                    ModuleType.Activity,
                    ModuleType.Vehicle,
                ],
                status: [Status.Approved, Status.Pending],
                visitType: mainBooking?.bookingInfo?.visitType,
            },
            { skip: !isExistingReservation },
        );
    const existingReservationOptions = allReservations
        ?.filter((reservation: any) => !reservation.mainBookingId)
        .map((reservation: any) => ({
            label: (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 0.5,
                        width: '100%',
                    }}
                >
                    <Box
                        component='img'
                        src={reservation.module.photo}
                        alt={reservation.moduleName}
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            objectFit: 'cover',
                        }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '14px' }}>
                            {reservation.moduleName} #{reservation.id}
                        </Typography>
                        <Typography fontSize='12px'>
                            {moment(reservation.startDate).format('DD-MM-YYYY')}{' '}
                            to{' '}
                            {moment(reservation.endDate).format('DD-MM-YYYY')}
                        </Typography>
                    </Box>
                    <Chip
                        label={reservation.module.type}
                        size='small'
                        sx={{
                            textTransform: 'capitalize',
                            flexShrink: 0,
                        }}
                    />
                </Box>
            ),
            value: reservation.id,
        }));

    const {
        data: existingReservationAsset,
        isFetching: isFetchingReservationAsset,
    } = useGetIndividualAssetQuery(
        `modules/${linkedReservation?.module?.id}?bookingId=${linkedBookingId}`,
        {
            skip: !linkedReservation?.module?.id,
        },
    );
    const { data: mainBookingAsset, isFetching: isFetchingMainBookingAsset } =
        useGetIndividualAssetQuery(`modules/${mainBooking?.module?.id}`, {
            skip: !mainBooking?.module?.id,
        });

    const contractors: ModuleContractor[] = [
        ...(mainBookingAsset?.contractors ?? []),
    ];

    const setMenuServiceTypes = Array.from(
        new Set(
            (mainBookingAsset?.setMenus || []).map(
                (setMenu: SetMenu) => setMenu.serviceType,
            ),
        ),
    ) as ServiceType[];

    const setMenus: SetMenuAsProvider[] = setMenuServiceTypes.map(
        (serviceType) => ({
            id:
                serviceType === ServiceType.Alcohol
                    ? OWNER_SET_MENU_CONTRACTOR_ID_ALCOHOL
                    : OWNER_SET_MENU_CONTRACTOR_ID_CATERING,
            name: `${mainBookingAsset?.owner?.name} (Set Menus)`,
            availableDays: [
                {
                    day: WeekDays.Monday,
                    periods: [],
                },
                {
                    day: WeekDays.Tuesday,
                    periods: [],
                },
                {
                    day: WeekDays.Wednesday,
                    periods: [],
                },
                {
                    day: WeekDays.Thursday,
                    periods: [],
                },
                {
                    day: WeekDays.Friday,
                    periods: [],
                },
                {
                    day: WeekDays.Saturday,
                    periods: [],
                },
                {
                    day: WeekDays.Sunday,
                    periods: [],
                },
            ],
            unavailablePeriods: [],
            services: { type: serviceType, items: [] },
            cutoff: 0,
        }),
    );

    const providers: (ModuleContractor | SetMenuAsProvider)[] = [
        ...contractors,
        ...setMenus,
    ];

    useEffect(() => {
        if (!open) {
            setAddOnIsSelected(false);
            return;
        }

        if (existingItem && !is7DayEdit) {
            const defaults = toFormDefaults(existingItem);
            reset(defaults);
            return;
        }

        if (!is7DayEdit) {
            reset({
                ...defaultValues,
                startDate: initialDate ?? '',
                endDate: initialDate ?? '',
                itineraryItemInfo: preselectedAddOn
                    ? {
                          ...defaultValues.itineraryItemInfo,
                          experienceId: preselectedAddOn.id,
                          itemType: preselectedAddOn.type,
                      }
                    : preselectedPartnerService
                      ? {
                            ...defaultValues.itineraryItemInfo,
                            itemType: ItineraryItemType.PartnerService,
                        }
                      : defaultValues.itineraryItemInfo,
                ...(preselectedPartnerService
                    ? { jobs: createEmptyPartnerJob(providers) }
                    : {}),
            });
            setAddOnIsSelected(!!preselectedAddOn || preselectedPartnerService);
        }
    }, [
        open,
        existingItem,
        initialDate,
        reset,
        is7DayEdit,
        preselectedAddOn,
        preselectedPartnerService,
    ]);

    useEffect(() => {
        if (!open || !preselectedPartnerService || is7DayEdit) return;
        setValue('jobs', createEmptyPartnerJob(providers), {
            shouldValidate: false,
        });
    }, [
        open,
        preselectedPartnerService,
        is7DayEdit,
        mainBookingAsset,
        setValue,
    ]);

    useEffect(() => {
        if (!is7DayEdit || !linkedReservation) return;
        const moduleType = linkedReservation?.bookingInfo?.moduleType;
        if (moduleType) {
            setValue('itineraryItemInfo.itemType', moduleType);
        }
        if (linkedReservation?.module?.id) {
            setValue(
                'itineraryItemInfo.experienceId',
                linkedReservation.module.id,
            );
        }
    }, [is7DayEdit, linkedReservation, setValue]);

    useEffect(() => {
        if (startDate && !methods.getValues('endDate')) {
            setValue('endDate', startDate, {
                shouldDirty: true,
                shouldValidate: true,
            });
            methods.clearErrors('endDate');
        }
    }, [startDate, setValue, methods]);

    const handleAttachmentUpload = async (
        files: File[],
    ): Promise<Media[] | undefined> => {
        try {
            const formData = new FormData();
            files.forEach((f) => formData.append('attachments', f));
            const response = await addItineraryAttachments(formData).unwrap();
            return response as unknown as Media[];
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to upload files');
            return undefined;
        }
    };

    const handleFormSubmit = async (data: FormData) => {
        if (
            data.itineraryItemInfo.itemType === ItineraryItemType.PartnerService
        ) {
            onSubmit(data);
            return;
        }
        const payload = buildPayload(data);
        onSubmit(payload);
    };

    const { data: experiences, isFetching } = useGetAllAssetsQuery(
        {
            url: '/modules',
            type: itemType,
        },
        {
            skip: !is7Day || is7DayEdit,
        },
    );

    const filteredExperiences = experiences?.filter(
        // if experience is an add on, ensure it is appropriate for the current reservation
        (experience: Experience) =>
            experience.availabilityMode !== AvailabilityMode.AddOn ||
            experience.addOnOf.some(
                (addOn) => addOn.id === mainBooking.module?.id,
            ),
    );

    const individualAsset = is7DayEdit
        ? existingReservationAsset
        : filteredExperiences?.find(
              (experience: any) => experience.id === experienceId,
          );

    const reservationFormState = useWatch({
        control,
        name: 'itineraryItemInfo.reservationFormState',
    });

    const handleReservationSubmit = async () => {
        if (reservationSubmitRef.current) {
            setIsReservationSubmitting(true);
            try {
                await reservationSubmitRef.current();
            } catch {
                // Errors are handled inside ReservationRequestContent
            } finally {
                setIsReservationSubmitting(false);
            }
        }
    };

    const handleAddExistingReservation = async () => {
        const request = {
            bookerType: mainBooking?.bookerType,
            mainBooking: { id: mainBooking.id },
        };

        const url = `/bookings/${existingReservation}`;

        try {
            await editReservation([url, request]).unwrap();
            toast.success('Reservation has been added to itinerary');
            onClose();
        } catch (error) {
            toast.error('Failed to add reservation to itinerary');
        }
    };

    const contractorJobsArray =
        jobs?.providerType === ProviderType.Contractor ? [jobs] : [];

    const selectedContractorsWithPolicy = contractors.filter(
        (contractor) =>
            contractor.cancellationPolicy &&
            contractor.id === jobs?.contractor?.id,
    );

    const showOptionalServices = shouldShowOptionalServicesSection(
        mainBookingAsset,
        mainBooking,
    );

    const isAddOnModal = isAddOnForm && !addOnIsSelected;

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth='md'
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: { xs: '100%', md: 940 },
                    maxHeight: '88vh',
                    borderRadius: 2,
                    overflow: 'hidden',
                },
            }}
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(handleFormSubmit)(e);
                    }}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <DialogTitle>
                        {isEditing
                            ? 'Edit Itinerary Item'
                            : isAddOnForm
                              ? 'Add-ons'
                              : 'Add Itinerary Item'}
                        <IconButton
                            onClick={() => onClose()}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <AutoHeightTransition>
                        <DialogContent
                            sx={{
                                width: '100%',
                                minWidth: { xs: '100%', md: 920 },
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                maxHeight: '70vh',
                                minHeight: '155px',
                            }}
                        >
                            {' '}
                            {isAddOnForm && !addOnIsSelected ? (
                                <>
                                    <Typography sx={{ mb: 2 }}>
                                        Enhance your stay with add-on
                                        experiences and partner services.
                                    </Typography>
                                    <>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                mb: 1,
                                            }}
                                        >
                                            Add-ons
                                        </Typography>
                                        <AddOnList
                                            experience={mainBookingAsset}
                                            handleSelect={(addOn) => {
                                                setValue(
                                                    'itineraryItemInfo.experienceId',
                                                    addOn.id,
                                                );
                                                setValue(
                                                    'itineraryItemInfo.itemType',
                                                    addOn.type,
                                                );
                                                setAddOnIsSelected(true);
                                            }}
                                        />
                                    </>
                                    {showOptionalServices && (
                                        <Box sx={{ mt: hasAddOns ? 3 : 0 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 'bold',
                                                    mb: 1,
                                                }}
                                            >
                                                Partner services
                                            </Typography>
                                            <OptionalServiceList
                                                experience={mainBookingAsset}
                                                onSelect={(service) => {
                                                    setValue(
                                                        'itineraryItemInfo.itemType',
                                                        ItineraryItemType.PartnerService,
                                                    );
                                                    setAddOnIsSelected(true);
                                                    setValue(
                                                        'jobs',
                                                        createEmptyPartnerJob(
                                                            providers,
                                                        ),
                                                    );
                                                }}
                                            />
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <>
                                    {' '}
                                    {!isAddOnForm && (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 400,
                                                maxWidth: 760,
                                                lineHeight: 1.5,
                                                pb: 3,
                                            }}
                                        >
                                            Enhance your reservation by adding
                                            itinerary items like flights,
                                            transfers, boats, and activities.
                                            This gives you a clear, at-a-glance
                                            view of everything planned for your
                                            stay-all in one place.
                                        </Typography>
                                    )}
                                    <Grid
                                        container
                                        spacing={1.5}
                                        sx={{
                                            pt: 0.5,
                                            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor:
                                                        theme.palette.form
                                                            .fieldBorder,
                                                },
                                            '& .MuiInputBase-root': {
                                                borderRadius: 1.25,
                                            },
                                        }}
                                    >
                                        {!is7DayEdit && !isAddOnForm && (
                                            <Grid item xs={12}>
                                                <FormSelect
                                                    name='itineraryItemInfo.itemType'
                                                    control={control}
                                                    label='Type'
                                                    options={ItemTypeOptions}
                                                    groupedMenu
                                                    disabled={isEditing}
                                                    onChange={(event) => {
                                                        const nextType = (
                                                            event.target as {
                                                                value: string;
                                                            }
                                                        ).value;
                                                        if (
                                                            nextType ===
                                                            ItineraryItemType.PartnerService
                                                        ) {
                                                            setValue(
                                                                'jobs',
                                                                createEmptyPartnerJob(
                                                                    providers,
                                                                ),
                                                                {
                                                                    shouldValidate: false,
                                                                },
                                                            );
                                                        } else {
                                                            unregister('jobs');
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        {!is7DayEdit &&
                                            isExistingReservation && (
                                                <Grid item xs={12}>
                                                    <FormSelect
                                                        name='existingReservation'
                                                        control={control}
                                                        label='Reservation'
                                                        options={
                                                            existingReservationOptions
                                                        }
                                                        disabled={isEditing}
                                                    />
                                                </Grid>
                                            )}

                                        {!is7Day &&
                                            !isExistingReservation &&
                                            !isPartnerService && (
                                                <Grid
                                                    item
                                                    container
                                                    spacing={2}
                                                >
                                                    <Grid item xs={12} sm={6}>
                                                        <FormDateFilter
                                                            name='startDate'
                                                            control={control}
                                                            label='Start Date'
                                                            defaultCalendarMonth={dayjs(
                                                                mainBooking?.startDate,
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormDateFilter
                                                            name='endDate'
                                                            control={control}
                                                            label='End Date'
                                                            defaultCalendarMonth={dayjs(
                                                                mainBooking?.endDate,
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormAutocomplete
                                                            name='startTime'
                                                            control={control}
                                                            label='Start Time'
                                                            options={TimeArray}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormAutocomplete
                                                            name='endTime'
                                                            control={control}
                                                            label='End Time'
                                                            options={TimeArray}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            )}
                                        {!is7DayEdit &&
                                            !addOnIsSelected &&
                                            [
                                                ItineraryItemType.Vehicle,
                                                ItineraryItemType.Boat,
                                                ItineraryItemType.Activity,
                                            ].includes(
                                                itemType as ItineraryItemType,
                                            ) && (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sx={{
                                                        minHeight: 330,
                                                    }}
                                                >
                                                    <ExperiencesList
                                                        experiences={
                                                            filteredExperiences ||
                                                            []
                                                        }
                                                        isLoading={isFetching}
                                                    />
                                                </Grid>
                                            )}

                                        {individualAsset &&
                                            isAddOnForm &&
                                            addOnIsSelected &&
                                            itemType !==
                                                ItineraryItemType.PartnerService && (
                                                <Grid item xs={12}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            pb: 2,
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <img
                                                            src={
                                                                individualAsset
                                                                    .photos[0]
                                                                    .src
                                                            }
                                                            style={{
                                                                aspectRatio: 1,
                                                                objectFit:
                                                                    'cover',
                                                                maxHeight: 75,
                                                                borderRadius: 8,
                                                            }}
                                                        />
                                                        <div>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                {
                                                                    individualAsset.name
                                                                }
                                                            </Typography>
                                                            <Typography variant='body2'>
                                                                {`Min/max hire period: ${formatHirePeriod(
                                                                    individualAsset?.minHireHours,
                                                                )} - ${formatMaxHirePeriod(
                                                                    individualAsset?.maxHireHours,
                                                                )}`}
                                                            </Typography>
                                                        </div>
                                                    </Box>
                                                </Grid>
                                            )}

                                        {itemType && (
                                            <>
                                                <Divider />
                                                {(() => {
                                                    switch (itemType) {
                                                        case ItineraryItemType.Flight:
                                                            return (
                                                                <FlightForm />
                                                            );
                                                        case ItineraryItemType.Event:
                                                            return (
                                                                <EventForm />
                                                            );
                                                        case ItineraryItemType.Restaurant:
                                                            return (
                                                                <RestaurantForm />
                                                            );
                                                        case ItineraryItemType.Transfer:
                                                            return (
                                                                <TransferForm />
                                                            );
                                                        case ItineraryItemType.Custom:
                                                            return (
                                                                <OtherForm />
                                                            );
                                                        case ItineraryItemType.Vehicle:
                                                        case ItineraryItemType.Boat:
                                                        case ItineraryItemType.Activity:
                                                            return (
                                                                <ReservationForm
                                                                    individualAsset={
                                                                        individualAsset
                                                                    }
                                                                    submitRef={
                                                                        reservationSubmitRef
                                                                    }
                                                                    mainBooking={
                                                                        mainBooking
                                                                    }
                                                                    onReservationSuccess={() => {
                                                                        onClose(
                                                                            'itinerary',
                                                                        );
                                                                    }}
                                                                    existingReservation={
                                                                        is7DayEdit
                                                                            ? linkedReservation
                                                                            : undefined
                                                                    }
                                                                />
                                                            );
                                                        default:
                                                            return null;
                                                    }
                                                })()}
                                            </>
                                        )}

                                        <Divider />
                                        {!is7Day &&
                                            !isExistingReservation &&
                                            !isPartnerService && (
                                                <>
                                                    {' '}
                                                    <Grid item xs={12}>
                                                        {' '}
                                                        <FormTextField
                                                            name='refNumber'
                                                            control={control}
                                                            label='Reference Number'
                                                            placeholder='e.g. REF-12345'
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FormTextField
                                                            name='notes'
                                                            control={control}
                                                            label='Notes'
                                                            multiline
                                                            rows={3}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Divider
                                                            sx={{
                                                                mt: 0.5,
                                                                mb: 0.25,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            variant='body3'
                                                            sx={{
                                                                fontWeight: 700,
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing:
                                                                    '0.08em',
                                                                color: 'text.secondary',
                                                                fontSize:
                                                                    '0.72rem',
                                                            }}
                                                        >
                                                            Attachments
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        {' '}
                                                        <FormUpload
                                                            name='attachments'
                                                            control={control}
                                                            onUpload={
                                                                handleAttachmentUpload
                                                            }
                                                            showFiles={false}
                                                            validation={{
                                                                acceptedTypes: {
                                                                    'image/*': [
                                                                        '.png',
                                                                        '.jpg',
                                                                        '.jpeg',
                                                                        '.gif',
                                                                        '.webp',
                                                                    ],
                                                                    'application/pdf':
                                                                        [
                                                                            '.pdf',
                                                                        ],
                                                                    'application/msword':
                                                                        [
                                                                            '.doc',
                                                                        ],
                                                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                                                                        [
                                                                            '.docx',
                                                                        ],
                                                                    'text/plain':
                                                                        [
                                                                            '.txt',
                                                                        ],
                                                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                                                                        [
                                                                            '.xlsx',
                                                                        ],
                                                                    'application/vnd.ms-excel':
                                                                        [
                                                                            '.xls',
                                                                        ],
                                                                    'text/csv':
                                                                        [
                                                                            '.csv',
                                                                        ],
                                                                },
                                                                maxFileSize:
                                                                    10 *
                                                                    1024 *
                                                                    1024,
                                                                maxFiles: 50,
                                                                orientation:
                                                                    'any',
                                                            }}
                                                            uploadText='Drop files here or click to browse'
                                                        />
                                                    </Grid>
                                                    {attachments.length > 0 && (
                                                        <Grid
                                                            item
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 0.75,
                                                                flexWrap:
                                                                    'wrap',
                                                            }}
                                                        >
                                                            {attachments.map(
                                                                (
                                                                    att: any,
                                                                    idx: number,
                                                                ) => (
                                                                    <Chip
                                                                        key={
                                                                            att.uuid ||
                                                                            att.id ||
                                                                            idx
                                                                        }
                                                                        icon={
                                                                            <AttachFile
                                                                                sx={{
                                                                                    fontSize: 14,
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            att.name ||
                                                                            'Attachment'
                                                                        }
                                                                        size='small'
                                                                        clickable={Boolean(
                                                                            att.src,
                                                                        )}
                                                                        onClick={() => {
                                                                            if (
                                                                                att.src
                                                                            ) {
                                                                                window.open(
                                                                                    att.src,
                                                                                    '_blank',
                                                                                    'noopener,noreferrer',
                                                                                );
                                                                            }
                                                                        }}
                                                                        onDelete={() => {
                                                                            const next =
                                                                                attachments.filter(
                                                                                    (
                                                                                        _: any,
                                                                                        i: number,
                                                                                    ) =>
                                                                                        i !==
                                                                                        idx,
                                                                                );
                                                                            setValue(
                                                                                'attachments',
                                                                                next,
                                                                                {
                                                                                    shouldDirty: true,
                                                                                    shouldValidate: true,
                                                                                },
                                                                            );
                                                                        }}
                                                                        deleteIcon={
                                                                            <Close
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '14px',
                                                                                }}
                                                                            />
                                                                        }
                                                                        sx={{
                                                                            fontSize: 11,
                                                                        }}
                                                                    />
                                                                ),
                                                            )}
                                                        </Grid>
                                                    )}
                                                </>
                                            )}
                                        {isPartnerService && (
                                            <Grid item xs={12}>
                                                <ReservationJobForm
                                                    name='jobs'
                                                    numberOfGuests={
                                                        mainBooking?.bookingInfo
                                                            ?.guests
                                                    }
                                                    experienceDetails={
                                                        mainBookingAsset
                                                    }
                                                    dateOptions={getDateArray(
                                                        mainBooking.startDate,
                                                        mainBooking.endDate,
                                                    )}
                                                    numberOfJobs={1}
                                                    index={0}
                                                    id={2}
                                                    jobsArray={[]}
                                                    providers={providers}
                                                    isItineraryForm={true}
                                                />
                                                {selectedContractorsWithPolicy.length >
                                                    0 && (
                                                    <ContractorPolicies
                                                        contractors={
                                                            contractors
                                                        }
                                                        jobs={
                                                            contractorJobsArray as Job[]
                                                        }
                                                        jobFieldPath='jobs'
                                                    />
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </DialogContent>
                    </AutoHeightTransition>

                    <DialogActions
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1,
                        }}
                    >
                        <Stack direction='row' spacing={1}>
                            {isAddOnForm && (
                                <Button
                                    onClick={() => {
                                        if (isAddOnModal) {
                                            onClose();
                                            setTab?.('details');
                                        } else {
                                            setAddOnIsSelected(false);
                                        }
                                    }}
                                    variant='outlined'
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: 96,
                                        fontWeight: 500,
                                    }}
                                >
                                    {isAddOnModal ? 'Maybe later' : 'Back'}
                                </Button>
                            )}
                            {!isAddOnForm && (
                                <Button
                                    onClick={() => onClose()}
                                    variant='outlined'
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: 96,
                                        fontWeight: 500,
                                    }}
                                >
                                    Close
                                </Button>
                            )}
                            {!isAddOnModal && (
                                <CustomButton
                                    variant='contained'
                                    color='primary'
                                    type={
                                        is7Day || isExistingReservation
                                            ? 'button'
                                            : 'submit'
                                    }
                                    disabled={
                                        is7Day
                                            ? isReservationSubmitting ||
                                              !experienceId ||
                                              (!reservationFormState &&
                                                  !linkedReservation)
                                            : isSubmitting ||
                                              (isEditing && !isDirty)
                                    }
                                    loading={
                                        is7Day
                                            ? isReservationSubmitting
                                            : isSubmitting
                                    }
                                    onClick={
                                        is7Day
                                            ? handleReservationSubmit
                                            : isExistingReservation
                                              ? handleAddExistingReservation
                                              : undefined
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: 140,
                                        fontWeight: 700,
                                        borderRadius: 1.25,
                                        px: 2.5,
                                    }}
                                >
                                    {isEditing
                                        ? 'Save Changes'
                                        : is7Day
                                          ? 'Reserve'
                                          : 'Add to Itinerary'}
                                </CustomButton>
                            )}
                        </Stack>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ItineraryItemForm;
